import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UseFormReturn, useWatch } from 'react-hook-form';
import isURL from 'validator/es/lib/isURL';

import { EditableInfoCard, SubmitProps } from '@/components/editable-info-card.js';
import { InfoKeyValue } from '@/components/info-key-value.js';
import { FormInputField } from '@/components/forms/form-input-field.js';
import { FormCheckbox } from '@/components/forms/form-checkbox.js';
import { FormMultiSelect } from '@/components/forms/form-multi-select.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';

import { getCoach, updateCoachProfile } from '@/lib/api-calls.js';
import { airServerErrorFrom } from '@/lib/error-handling.js';
import { maxZoomIdLength, minZoomIdLength, zoomIdExtractRegex, zoomIdValidationRegex } from '@/lib/string-utils.js';
import {
  ClassEligibility,
  FullCoach,
  BackgroundCheckStatus,
  zoomTypeSchema,
  ZoomLicenseOptions,
  zoomNoLicenseString,
  ZoomType,
  ZoomNoLicenseString,
} from '@airreading/common/data';
import { classEligibilityOptions, teacherBackgroundCheckStatusOptions } from '@/lib/coach-utils.js';
import {
  buildClassEligibilitySelectOption,
  buildBackgroundCheckStatusSelectOption,
  multiSelectOptions,
} from '@/components/ui/multi-select-utils.js';

import { ClassEligibilityBadgeList } from './class-eligibility-badge-list.js';
import { BackgroundCheckStatusBadgeList } from './background-check-status-badge-list.js';
import { Check, X } from 'lucide-react';
import { FormSingleSelect } from '@/components/forms/form-single-select.js';
import { getZoomLicenseTypeLabel } from '@/lib/zoom-utils.js';

const editSetupFormSchema = z.object({
  zoomLink: z
    .string()
    .optional()
    .refine((val) => !val || isURL(val), { message: 'Invalid URL' })
    .refine(
      (val) => {
        if (!val) return true;
        const { isValid } = extractZoomId(val);
        return isValid;
      },
      { message: 'The Zoom Meeting ID should be a number between 9 and 11 digits' },
    ),
  zoomId: z
    .string()
    .optional()
    .refine((val) => !val || zoomIdValidationRegex.test(val), { message: 'Invalid Zoom ID format' }),
  zoomLicense: z
    .union([zoomTypeSchema, z.literal(zoomNoLicenseString)])
    .nullable()
    .optional(),
  JPSGoogleMeetUrl: z
    .string()
    .optional()
    .refine((val) => !val || isURL(val), { message: 'Invalid URL' }),
  airReadingGoogleMeetUrl: z
    .string()
    .optional()
    .refine((val) => !val || isURL(val), { message: 'Invalid URL' }),
  zoomTechCheck: z.boolean().optional(),
  googleMeetTechCheck: z.boolean().optional(),
  backgroundCheckStatuses: multiSelectOptions,
  classEligibilities: multiSelectOptions,
  floridaTutorId: z.string().optional(),
});

type EditSetupInfoFormSchema = z.infer<typeof editSetupFormSchema>;

interface ReadingSetupCardProps {
  coach: FullCoach;
  userId: string;
}

function extractZoomId(zoomLink: string | undefined): { id: string; isValid: boolean } {
  if (!zoomLink) return { id: '', isValid: false };

  const match = zoomIdExtractRegex.exec(zoomLink);
  const extractedId = match?.[1] ?? '';

  const isValid =
    extractedId.length >= minZoomIdLength && extractedId.length <= maxZoomIdLength && /^\d+$/.test(extractedId);

  return {
    id: isValid ? extractedId : '',
    isValid,
  };
}

function ZoomIdDisplay({ form, editing }: { form: UseFormReturn<EditSetupInfoFormSchema>; editing: boolean }) {
  const zoomId = useWatch({ control: form.control, name: 'zoomId' });
  const zoomLink = useWatch({ control: form.control, name: 'zoomLink' });

  const displayValue = !zoomLink ? '' : zoomId || '-';

  return (
    <InfoKeyValue
      label="Zoom ID"
      value={displayValue}
      edit={{
        editing,
        editModeComponent: <FormInputField name="zoomId" label="Zoom ID" form={form} inputProps={{ disabled: true }} />,
      }}
    />
  );
}

export function ReadingSetupCard({ coach, userId }: ReadingSetupCardProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync: updateCoachProfileMutate } = useMutation({ mutationFn: updateCoachProfile });

  const onSubmit = async ({ formData, disableEditing }: SubmitProps<EditSetupInfoFormSchema>) => {
    const {
      zoomLink,
      zoomId,
      zoomLicense,
      JPSGoogleMeetUrl,
      airReadingGoogleMeetUrl,
      zoomTechCheck,
      googleMeetTechCheck,
      backgroundCheckStatuses,
      classEligibilities,
      floridaTutorId,
    } = formData;

    try {
      await updateCoachProfileMutate({
        userId,
        data: {
          zoomPersonalMeetingUrl: zoomLink,
          zoomPersonalMeetingId: zoomId,
          zoomType: zoomLicense === zoomNoLicenseString ? null : zoomLicense,
          JPSGoogleMeetUrl: JPSGoogleMeetUrl,
          airReadingGoogleMeetUrl: airReadingGoogleMeetUrl,
          zoomTechCheck: zoomTechCheck,
          googleMeetTechCheck: googleMeetTechCheck,
          backgroundCheckStatuses: backgroundCheckStatuses.map((option) => option.value as BackgroundCheckStatus),
          classEligibilities: classEligibilities.map((option) => option.value as ClassEligibility),
          floridaTutorId: floridaTutorId?.trim() || null,
        },
      });

      toast({ title: 'Reading setup info updated' });
      disableEditing();
      queryClient.invalidateQueries(getCoach(userId));
    } catch (e) {
      toast({
        title: airServerErrorFrom(e)?.userFacingMessage || 'Failed to update setup info',
      });
    }
  };

  const hasZoomPMI = Boolean(coach?.zoomPersonalMeetingUrl?.trim());

  return (
    <EditableInfoCard
      headerText="Air Reading Setup"
      formSchema={editSetupFormSchema}
      onSubmit={onSubmit}
      defaultValues={{
        zoomLink: coach?.zoomPersonalMeetingUrl || undefined,
        zoomId: coach?.zoomPersonalMeetingId || undefined,
        zoomLicense: (coach?.zoomType ?? zoomNoLicenseString) as ZoomType | ZoomNoLicenseString,
        JPSGoogleMeetUrl: coach?.JPSGoogleMeetUrl || undefined,
        airReadingGoogleMeetUrl: coach?.airReadingGoogleMeetUrl || undefined,
        zoomTechCheck: coach?.zoomTechCheck || undefined,
        googleMeetTechCheck: coach?.googleMeetTechCheck || undefined,
        backgroundCheckStatuses: coach?.backgroundCheckStatuses?.map(buildBackgroundCheckStatusSelectOption),
        classEligibilities: coach?.classEligibilities?.map(buildClassEligibilitySelectOption),
        floridaTutorId: coach?.floridaTutorId || '',
      }}
    >
      {({ form, editing }) => {
        return (
          <>
            <InfoKeyValue
              label="Zoom link"
              value={coach?.zoomPersonalMeetingUrl || zoomNoLicenseString}
              labelClassName="break-words max-w-[200px] 2xl:max-w-none whitespace-pre-line"
              edit={{
                editing,
                editModeComponent: (
                  <FormInputField
                    name="zoomLink"
                    label="Zoom link"
                    form={form}
                    inputProps={{
                      onBlur: (e) => {
                        const { id } = extractZoomId(e.target.value);
                        form.setValue('zoomId', id);
                      },
                    }}
                  />
                ),
              }}
            />
            <ZoomIdDisplay form={form} editing={editing} />
            <InfoKeyValue
              label="Zoom license"
              value={getZoomLicenseTypeLabel(coach?.zoomType)}
              edit={{
                editing,
                editModeComponent: (
                  <FormSingleSelect
                    name="zoomLicense"
                    label="Zoom license"
                    form={form}
                    options={ZoomLicenseOptions}
                    renderOption={(status) => getZoomLicenseTypeLabel(status)}
                    triggerProps={{ className: 'h-9' }}
                    isOptionDisabled={(option) => {
                      const value = option as ZoomType | ZoomNoLicenseString;
                      return hasZoomPMI ? value === zoomNoLicenseString : value !== zoomNoLicenseString;
                    }}
                  />
                ),
              }}
            />
            <InfoKeyValue
              label="JPS Google Meet link"
              value={coach?.JPSGoogleMeetUrl || '-'}
              labelClassName="break-words max-w-[200px] 2xl:max-w-none whitespace-pre-line"
              edit={{
                editing,
                editModeComponent: <FormInputField name="JPSGoogleMeetUrl" label="JPS Google Meet link" form={form} />,
              }}
            />
            <InfoKeyValue
              label="Air Google Meet link"
              value={coach?.airReadingGoogleMeetUrl || '-'}
              labelClassName="break-words max-w-[190px] 2xl:max-w-none whitespace-pre-line"
              edit={{
                editing,
                editModeComponent: (
                  <FormInputField name="airReadingGoogleMeetUrl" label="Air Google Meet link" form={form} />
                ),
              }}
            />
            <InfoKeyValue
              label="Zoom tech check"
              value={coach?.zoomTechCheck ? <Check /> : <X />}
              edit={{
                editing,
                editModeComponent: <FormCheckbox name="zoomTechCheck" label="Zoom tech check" form={form} />,
              }}
            />
            <InfoKeyValue
              label="Google Meet tech check"
              value={coach?.googleMeetTechCheck ? <Check /> : <X />}
              edit={{
                editing,
                editModeComponent: (
                  <FormCheckbox name="googleMeetTechCheck" label="Google Meet tech check" form={form} />
                ),
              }}
            />
            <InfoKeyValue
              label="Background check"
              value={
                coach?.backgroundCheckStatuses?.length ? (
                  <BackgroundCheckStatusBadgeList statuses={coach.backgroundCheckStatuses} />
                ) : (
                  '-'
                )
              }
              edit={{
                editing,
                editModeComponent: (
                  <FormMultiSelect
                    form={form}
                    name="backgroundCheckStatuses"
                    label="Background check"
                    multipleSelectorProps={{
                      options: teacherBackgroundCheckStatusOptions,
                      placeholder: 'Select background check statuses...',
                    }}
                  />
                ),
              }}
            />
            <InfoKeyValue
              label="Class eligibility"
              value={
                coach?.classEligibilities?.length ? (
                  <ClassEligibilityBadgeList eligibilities={coach.classEligibilities} />
                ) : (
                  '-'
                )
              }
              edit={{
                editing,
                editModeComponent: (
                  <FormMultiSelect
                    form={form}
                    name="classEligibilities"
                    label="Class eligibility"
                    multipleSelectorProps={{
                      options: classEligibilityOptions,
                      placeholder: 'Select eligibilities...',
                    }}
                  />
                ),
              }}
            />
            <InfoKeyValue
              label="Florida tutor ID"
              value={coach?.floridaTutorId || '-'}
              labelClassName="break-all"
              edit={{
                editing,
                editModeComponent: <FormInputField name="floridaTutorId" label="Florida tutor ID" form={form} />,
              }}
            />
          </>
        );
      }}
    </EditableInfoCard>
  );
}
